import * as React from 'react';

import { cn } from '@/lib/utils';
import { Badge } from './badge';
import { Input } from './input';
import { Label } from './label';

// ---------------------------------------------------------------------------
// Pure calculation helpers (exported for testing)
// ---------------------------------------------------------------------------

/**
 * Kiszámítja a bruttó árat a nettó és az áfakulcs alapján.
 * HUF esetén Math.round() kerekítés.
 */
export function calcGross(net: number, vatRate: number): number {
  return Math.round(net * (1 + vatRate / 100));
}

/**
 * Kiszámítja a nettó árat a bruttó és az áfakulcs alapján.
 * HUF esetén Math.round() kerekítés.
 */
export function calcNet(gross: number, vatRate: number): number {
  return Math.round(gross / (1 + vatRate / 100));
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface PriceInputProps {
  /** Nettó érték — '' ha üres */
  netValue: number | '';
  /** Bruttó érték — '' ha üres */
  grossValue: number | '';
  /** ÁFA kulcs: 0 | 5 | 18 | 27 */
  vatRate: number;
  /** Meghívódik nettó szerkesztésekor: (net, gross) */
  onNetChange: (net: number, gross: number) => void;
  /** Meghívódik bruttó szerkesztésekor: (gross, net) */
  onGrossChange: (gross: number, net: number) => void;
  /** Pénznem jel (default: 'Ft') */
  currency?: string | undefined;
  /** Tiltja mindkét mezőt */
  disabled?: boolean | undefined;
  /** Nettó mező felirata (i18n: t('priceInput.netLabel')) */
  netLabel?: string | undefined;
  /** Bruttó mező felirata (i18n: t('priceInput.grossLabel')) */
  grossLabel?: string | undefined;
  /** ÁFA badge felirata (i18n: t('priceInput.vatRate')) */
  vatLabel?: string | undefined;
  /** Extra className az outer container-re */
  className?: string | undefined;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function PriceInput({
  netValue,
  grossValue,
  vatRate,
  onNetChange,
  onGrossChange,
  currency = 'Ft',
  disabled = false,
  netLabel = 'Nettó',
  grossLabel = 'Bruttó',
  vatLabel = 'ÁFA',
  className,
}: PriceInputProps) {
  /**
   * lastEdited: melyik mezőt szerkesztette utoljára a felhasználó.
   * vatRate változásakor ebből számítjuk újra a másik mezőt.
   */
  const lastEdited = React.useRef<'net' | 'gross'>('net');

  // Stable refs for callbacks — prevents useEffect infinite loop
  const onNetChangeRef = React.useRef(onNetChange);
  const onGrossChangeRef = React.useRef(onGrossChange);
  onNetChangeRef.current = onNetChange;
  onGrossChangeRef.current = onGrossChange;

  // vatRate változásakor újraszámítjuk a párját
  const prevVatRate = React.useRef<number>(vatRate);
  React.useEffect(() => {
    if (prevVatRate.current === vatRate) return;
    prevVatRate.current = vatRate;

    if (lastEdited.current === 'net' && netValue !== '') {
      const newGross = calcGross(netValue, vatRate);
      onNetChangeRef.current(netValue, newGross);
    } else if (lastEdited.current === 'gross' && grossValue !== '') {
      const newNet = calcNet(grossValue, vatRate);
      onGrossChangeRef.current(grossValue, newNet);
    }
  }, [vatRate, netValue, grossValue]);

  // --- Handlers ---

  const handleNetChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    lastEdited.current = 'net';
    const raw = e.target.value;
    if (raw === '' || raw === '-') {
      // üres → ne számítsunk
      return;
    }
    const net = parseInt(raw, 10);
    if (!Number.isFinite(net) || net < 0) return;
    const gross = calcGross(net, vatRate);
    onNetChange(net, gross);
  };

  const handleGrossChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    lastEdited.current = 'gross';
    const raw = e.target.value;
    if (raw === '' || raw === '-') {
      return;
    }
    const gross = parseInt(raw, 10);
    if (!Number.isFinite(gross) || gross < 0) return;
    const net = calcNet(gross, vatRate);
    onGrossChange(gross, net);
  };

  const netId = React.useId();
  const grossId = React.useId();

  return (
    <div className={cn('flex flex-row items-end gap-2', className)}>
      {/* Nettó mező */}
      <div className="flex flex-1 flex-col gap-1.5">
        <Label htmlFor={netId} className="text-muted-foreground text-sm font-medium">
          {netLabel}
        </Label>
        <div className="relative flex items-center">
          <Input
            id={netId}
            type="number"
            inputMode="numeric"
            min={0}
            value={netValue}
            onChange={handleNetChange}
            disabled={disabled}
            className="pr-8"
            aria-label={netLabel}
          />
          <span
            className="text-muted-foreground pointer-events-none absolute right-2 text-xs"
            aria-hidden="true"
          >
            {currency}
          </span>
        </div>
      </div>

      {/* ÁFA badge */}
      <div className="flex h-9 items-center pb-0">
        <Badge variant="secondary" className="whitespace-nowrap text-xs">
          {vatLabel} {vatRate}%
        </Badge>
      </div>

      {/* Bruttó mező */}
      <div className="flex flex-1 flex-col gap-1.5">
        <Label htmlFor={grossId} className="text-muted-foreground text-sm font-medium">
          {grossLabel}
        </Label>
        <div className="relative flex items-center">
          <Input
            id={grossId}
            type="number"
            inputMode="numeric"
            min={0}
            value={grossValue}
            onChange={handleGrossChange}
            disabled={disabled}
            className="pr-8"
            aria-label={grossLabel}
          />
          <span
            className="text-muted-foreground pointer-events-none absolute right-2 text-xs"
            aria-hidden="true"
          >
            {currency}
          </span>
        </div>
      </div>
    </div>
  );
}
